import { useState, useEffect, useCallback } from 'react';
import supportApiService from '../../services/supportApiService';
import ActivityMetadataViewer from '../common/ActivityMetadataViewer';

const SupportActivityLogs = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [moduleOptions, setModuleOptions] = useState({ modules: [], typesByModule: {}, statuses: [] });

  // Local input state for smooth typing & debouncing
  const [searchInput, setSearchInput] = useState('');
  const [usernameInput, setUsernameInput] = useState('');

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    username: '',
    module: '',
    activityType: '',
    status: '',
    datePreset: 'last7days',
    startDate: '',
    endDate: '',
    page: 0,
    size: 20,
    sortBy: 'createdAt',
    sortDir: 'DESC'
  });

  // Pagination meta
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 20,
    hasNext: false,
    hasPrevious: false
  });

  // Modal detail state
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Helper to format a local Date object into 'YYYY-MM-DDTHH:mm:ss'
  const toLocalISOString = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Initialize date ranges based on preset using local time boundaries
  const applyDatePreset = (preset) => {
    const now = new Date();
    let start = '';
    let end = '';

    if (preset === 'today') {
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      start = toLocalISOString(todayStart);
      end = toLocalISOString(todayEnd);
    } else if (preset === 'yesterday') {
      const yesterdayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
      const yesterdayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
      start = toLocalISOString(yesterdayStart);
      end = toLocalISOString(yesterdayEnd);
    } else if (preset === 'last7days') {
      const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      start = toLocalISOString(sevenDaysAgo);
      end = toLocalISOString(todayEnd);
    } else if (preset === 'last30days') {
      const thirtyDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0);
      const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      start = toLocalISOString(thirtyDaysAgo);
      end = toLocalISOString(todayEnd);
    } else if (preset === 'all') {
      start = '';
      end = '';
    }

    return { start, end };
  };

  // Debounce search and username typing (350ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if (prev.searchTerm === searchInput && prev.username === usernameInput) {
          return prev;
        }
        return {
          ...prev,
          searchTerm: searchInput,
          username: usernameInput,
          page: 0
        };
      });
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput, usernameInput]);

  // Load Module & Type options and initial stats
  useEffect(() => {
    const loadOptionsAndStats = async () => {
      try {
        const [optionsRes, statsRes] = await Promise.all([
          supportApiService.getActivityModuleOptions(),
          supportApiService.getActivityStats()
        ]);
        if (optionsRes.success && optionsRes.data) {
          setModuleOptions(optionsRes.data);
        }
        if (statsRes.success && statsRes.data) {
          setStats(statsRes.data);
        }
      } catch (err) {
        console.error('Failed to load support initial options/stats:', err);
      }
    };
    loadOptionsAndStats();
  }, []);

  // Fetch Logs with active filters
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: filters.page,
        size: filters.size,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir
      };

      if (filters.searchTerm) params.searchTerm = filters.searchTerm;
      if (filters.username) params.username = filters.username;
      if (filters.module) params.module = filters.module;
      if (filters.activityType) params.activityType = filters.activityType;
      if (filters.status) params.status = filters.status;

      let sDate = filters.startDate;
      let eDate = filters.endDate;
      if (filters.datePreset && filters.datePreset !== 'custom') {
        const calculated = applyDatePreset(filters.datePreset);
        sDate = calculated.start;
        eDate = calculated.end;
      }
      if (sDate) params.startDate = sDate;
      if (eDate) params.endDate = eDate;

      const response = await supportApiService.getActivityLogs(params);
      if (response.success) {
        setLogs(response.data || []);
        setPagination({
          currentPage: response.currentPage ?? 0,
          totalPages: response.totalPages ?? 0,
          totalElements: response.totalElements ?? 0,
          pageSize: response.pageSize ?? 20,
          hasNext: response.hasNext ?? false,
          hasPrevious: response.hasPrevious ?? false
        });
      } else {
        setError(response.message || 'Failed to fetch activity logs');
      }
    } catch (err) {
      setError(err.message || 'Error loading activity logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handle Preset Change
  const handlePresetChange = (preset) => {
    if (preset === 'custom') {
      setFilters((prev) => ({ ...prev, datePreset: 'custom', page: 0 }));
    } else {
      const { start, end } = applyDatePreset(preset);
      setFilters((prev) => ({
        ...prev,
        datePreset: preset,
        startDate: start,
        endDate: end,
        page: 0
      }));
    }
  };

  // Reset Filters
  const handleReset = () => {
    setSearchInput('');
    setUsernameInput('');
    const { start, end } = applyDatePreset('last7days');
    setFilters({
      searchTerm: '',
      username: '',
      module: '',
      activityType: '',
      status: '',
      datePreset: 'last7days',
      startDate: start,
      endDate: end,
      page: 0,
      size: 20,
      sortBy: 'createdAt',
      sortDir: 'DESC'
    });
  };

  // View Log Detail Modal
  const handleViewDetail = async (id) => {
    try {
      setLoadingDetail(true);
      setModalOpen(true);
      setSelectedLog(null);
      const res = await supportApiService.getActivityLogById(id);
      if (res.success && res.data) {
        setSelectedLog(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch log details:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Export CSV
  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {};
      if (filters.searchTerm) params.searchTerm = filters.searchTerm;
      if (filters.username) params.username = filters.username;
      if (filters.module) params.module = filters.module;
      if (filters.activityType) params.activityType = filters.activityType;
      if (filters.status) params.status = filters.status;

      let sDate = filters.startDate;
      let eDate = filters.endDate;
      if (filters.datePreset && filters.datePreset !== 'custom') {
        const calculated = applyDatePreset(filters.datePreset);
        sDate = calculated.start;
        eDate = calculated.end;
      }
      if (sDate) params.startDate = sDate;
      if (eDate) params.endDate = eDate;

      const blob = await supportApiService.exportActivityLogs(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `support_activity_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowExportModal(false);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export activity logs: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  // Dynamic available types based on selected module
  const availableTypes = filters.module && moduleOptions.typesByModule[filters.module]
    ? moduleOptions.typesByModule[filters.module]
    : [];

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <i className="fas fa-history text-emerald-600"></i>
            Activity Logs
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Audit and monitor your own actions and all agent activities
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-all"
            title="Refresh logs"
          >
            <i className={`fas fa-sync-alt ${loading ? 'animate-spin' : ''}`}></i>
            Refresh
          </button>

          <button
            onClick={() => setShowExportModal(true)}
            disabled={exporting || loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 shadow-sm transition-all disabled:opacity-50"
          >
            <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Scope Banner */}
      <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between text-emerald-900 text-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center shrink-0">
            <i className="fas fa-shield-alt text-emerald-700"></i>
          </div>
          <div>
            <span className="font-semibold">Support Executive View Active:</span> You are viewing your own activity logs and all registered Agent activities. Administrator activities and other support staff logs are strictly excluded.
          </div>
        </div>
      </div>

      {/* Summary Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Scoped Activities</p>
              <h3 className="text-2xl font-bold text-gray-900 mt-1">{stats.totalActivities?.toLocaleString() || 0}</h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl">
              <i className="fas fa-list-check"></i>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Failed (Last 24h)</p>
              <h3 className="text-2xl font-bold text-rose-600 mt-1">{stats.failedLast24Hours?.toLocaleString() || 0}</h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking & Cancellations</p>
              <h3 className="text-2xl font-bold text-emerald-600 mt-1">
                {((stats.activitiesByModule?.BOOKING || 0) + (stats.activitiesByModule?.CANCELLATION || 0)).toLocaleString()}
              </h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
              <i className="fas fa-hotel"></i>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Support Tickets</p>
              <h3 className="text-2xl font-bold text-amber-600 mt-1">
                {(stats.activitiesByModule?.SUPPORT_TICKETS || 0).toLocaleString()}
              </h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl">
              <i className="fas fa-headset"></i>
            </div>
          </div>
        </div>
      )}

      {/* Filter Control Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 md:p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Free Text Search */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Search Term
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search description, IP, trace ID..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <i className="fas fa-search absolute left-3 top-2.5 text-gray-400 text-sm"></i>
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
          </div>

          {/* Username Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Username / Email
            </label>
            <div className="relative">
              <input
                type="text"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                placeholder="Filter by agent email..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <i className="fas fa-user absolute left-3 top-2.5 text-gray-400 text-sm"></i>
              {usernameInput && (
                <button
                  onClick={() => setUsernameInput('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value, page: 0 }))}
              className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All Statuses</option>
              {moduleOptions.statuses.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Module Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Module
            </label>
            <select
              value={filters.module}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  module: e.target.value,
                  activityType: '',
                  page: 0
                }))
              }
              className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">All Modules</option>
              {moduleOptions.modules.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* Activity Type Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Activity Type
            </label>
            <select
              value={filters.activityType}
              onChange={(e) => setFilters((prev) => ({ ...prev, activityType: e.target.value, page: 0 }))}
              disabled={!filters.module}
              className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">All Types</option>
              {availableTypes.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.description}
                </option>
              ))}
            </select>
          </div>

          {/* Date Presets */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Time Range
            </label>
            <select
              value={filters.datePreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Reset Filters Button */}
          <div className="flex items-end">
            <button
              onClick={handleReset}
              className="w-full py-2 px-4 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <i className="fas fa-undo text-xs"></i>
              Reset Filters
            </button>
          </div>
        </div>

        {/* Custom Date Pickers */}
        {filters.datePreset === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                Start Date & Time
              </label>
              <input
                type="datetime-local"
                value={filters.startDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value, page: 0 }))}
                className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                End Date & Time
              </label>
              <input
                type="datetime-local"
                value={filters.endDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value, page: 0 }))}
                className="w-full py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Logs Table Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {error && (
          <div className="p-4 bg-rose-50 border-b border-rose-100 text-rose-700 text-sm flex items-center gap-2">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase tracking-wider font-semibold">
                <th className="py-3 px-4">Timestamp</th>
                <th className="py-3 px-4">User & Role</th>
                <th className="py-3 px-4">Module</th>
                <th className="py-3 px-4">Activity</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4">Client IP</th>
                <th className="py-3 px-4">Duration</th>
                <th className="py-3 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="9" className="py-12 text-center text-gray-500">
                    <div className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-600 border-t-transparent"></div>
                      Loading activity logs...
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="py-12 text-center text-gray-400">
                    <i className="fas fa-inbox text-3xl mb-2 block text-gray-300"></i>
                    No activity logs found matching the selected filter criteria.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    {/* Timestamp */}
                    <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-500 font-mono">
                      {formatDate(log.createdAt)}
                    </td>

                    {/* User & Role */}
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{log.username || 'Anonymous'}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                          log.userRole?.includes('SUPPORT') ? 'bg-emerald-500' : 'bg-blue-500'
                        }`}></span>
                        {log.userRole?.replace('ROLE_', '') || 'UNKNOWN'}
                      </div>
                    </td>

                    {/* Module */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {log.moduleDisplayName || log.module}
                      </span>
                    </td>

                    {/* Activity Type */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="text-xs font-mono text-gray-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                        {log.activityType}
                      </span>
                    </td>

                    {/* Description */}
                    <td className="py-3 px-4 max-w-xs truncate text-gray-800" title={log.description}>
                      {log.description || '-'}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          log.status === 'SUCCESS'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-rose-100 text-rose-800'
                        }`}
                      >
                        <i className={`fas fa-circle text-[6px] mr-1.5 ${
                          log.status === 'SUCCESS' ? 'text-emerald-500' : 'text-rose-500'
                        }`}></i>
                        {log.status}
                      </span>
                    </td>

                    {/* Client IP */}
                    <td className="py-3 px-4 whitespace-nowrap text-xs font-mono text-gray-500">
                      {log.ipAddress || '-'}
                    </td>

                    {/* Duration */}
                    <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-500">
                      {log.durationMs != null ? `${log.durationMs}ms` : '-'}
                    </td>

                    {/* Action */}
                    <td className="py-3 px-4 text-center whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetail(log.id)}
                        className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                        title="View details"
                      >
                        <i className="fas fa-eye text-sm"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!loading && logs.length > 0 && (
          <div className="py-3 px-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
            <div>
              Showing <span className="font-semibold">{pagination.currentPage * pagination.pageSize + 1}</span> to{' '}
              <span className="font-semibold">
                {Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalElements)}
              </span>{' '}
              of <span className="font-semibold">{pagination.totalElements.toLocaleString()}</span> entries
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={!pagination.hasPrevious || loading}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 font-medium transition-all"
              >
                Previous
              </button>

              <span className="px-2 font-medium">
                Page {pagination.currentPage + 1} of {Math.max(1, pagination.totalPages)}
              </span>

              <button
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={!pagination.hasNext || loading}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 font-medium transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Detail Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-fade-in">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center gap-2">
                <i className="fas fa-info-circle text-emerald-600 text-lg"></i>
                <h3 className="text-lg font-bold text-gray-900">Activity Log Details</h3>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1 rounded-lg hover:bg-gray-100"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-4">
              {loadingDetail ? (
                <div className="py-12 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent mx-auto mb-2"></div>
                  Loading details...
                </div>
              ) : selectedLog ? (
                <>
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500 block">Log ID</span>
                      <span className="font-mono font-semibold text-gray-900">{selectedLog.id}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Timestamp</span>
                      <span className="font-semibold text-gray-900">{formatDate(selectedLog.createdAt)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Username</span>
                      <span className="font-semibold text-gray-900">{selectedLog.username || 'Anonymous'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">User Role</span>
                      <span className="font-semibold text-gray-900">{selectedLog.userRole || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Module</span>
                      <span className="font-semibold text-gray-900">{selectedLog.moduleDisplayName || selectedLog.module}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Activity Type</span>
                      <span className="font-semibold text-gray-900">{selectedLog.activityType}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Status</span>
                      <span
                        className={`inline-block font-semibold px-2 py-0.5 rounded text-xs mt-0.5 ${
                          selectedLog.status === 'SUCCESS' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                        }`}
                      >
                        {selectedLog.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Duration</span>
                      <span className="font-semibold text-gray-900">
                        {selectedLog.durationMs != null ? `${selectedLog.durationMs} ms` : '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">IP Address</span>
                      <span className="font-mono text-gray-900">{selectedLog.ipAddress || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Trace ID</span>
                      <span className="font-mono text-gray-900">{selectedLog.traceId || '-'}</span>
                    </div>
                  </div>

                  <div>
                    <span className="text-xs text-gray-500 block mb-1">Description</span>
                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-800 border border-gray-100">
                      {selectedLog.description || '-'}
                    </div>
                  </div>

                  {selectedLog.errorMessage && (
                    <div>
                      <span className="text-xs text-rose-600 font-semibold block mb-1">Error Message</span>
                      <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs font-mono text-rose-800 break-words">
                        {selectedLog.errorMessage}
                      </div>
                    </div>
                  )}

                  {/* Formatted Metadata Section */}
                  <ActivityMetadataViewer
                    metadata={selectedLog.metadata}
                    theme="emerald"
                    title="Activity Metadata"
                  />
                </>
              ) : (
                <div className="text-center text-gray-500 py-6">Could not load log details.</div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Confirmation Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl flex flex-col animate-fade-in">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-emerald-50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                  <i className="fas fa-file-csv text-base"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Export Activity Logs</h3>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold p-1 rounded-lg hover:bg-gray-100"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to export and download the activity logs as a <strong>CSV</strong> file?
              </p>

              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 space-y-2 text-xs text-gray-700">
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Estimated Records:</span>
                  <span className="font-bold text-emerald-700">{pagination.totalElements.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 font-medium">Time Range:</span>
                  <span className="font-semibold text-gray-800 capitalize">
                    {filters.datePreset === 'all'
                      ? 'All Time'
                      : filters.datePreset === 'custom'
                      ? 'Custom Range'
                      : filters.datePreset}
                  </span>
                </div>
                {filters.module && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Module:</span>
                    <span className="font-semibold text-gray-800">{filters.module}</span>
                  </div>
                )}
                {filters.status && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Status:</span>
                    <span className="font-semibold text-gray-800">{filters.status}</span>
                  </div>
                )}
                {filters.username && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 font-medium">Agent/User:</span>
                    <span className="font-semibold text-gray-800">{filters.username}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 shadow-sm transition-all disabled:opacity-50"
              >
                <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-download'}`}></i>
                {exporting ? 'Downloading...' : 'Yes, Download CSV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportActivityLogs;
